import { useState, useEffect, useContext, useMemo } from "react";
import { Task } from "@/bryntum-lib/models/Task";
import { TaskRenderer } from "@/modern/components/task-renderer";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { Search } from "lucide-react";
import { cn } from "@/modern/utils";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { isTaskScheduledInPastOrToday } from "@/other/utils/incentiveUtils.jsx";
import {
  SpotIncentivePickerShell,
  type SpotIncentivePickerPresentation,
} from "./SpotIncentivePickerShell";

interface TaskSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTaskId: number | null;
  onTaskSelect: (taskId: number) => void;
  editorInstance?: unknown;
  presentation?: SpotIncentivePickerPresentation;
}

const SpotIncentiveTaskSelector = ({
  open,
  onOpenChange,
  selectedTaskId,
  onTaskSelect,
  presentation = "dialog",
}: TaskSelectorProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { project } = useContext(BryntumContext) as any;
  const [searchQuery, setSearchQuery] = useState("");
  const [localSelectedId, setLocalSelectedId] = useState<number | null>(selectedTaskId);
  const isInline = presentation === "inline";

  useEffect(() => {
    if (open) setLocalSelectedId(selectedTaskId);
  }, [open, selectedTaskId]);

  const tasks = useMemo(
    () =>
      project?.extraTaskStore?.query((record: Task) => {
        if (!isTaskScheduledInPastOrToday(record)) return false;
        const selectedBoard = getSavedBoardSelection();
        const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
        return !record.isPhantom && (canShowArchivedRecords || !record.archived);
      }, true) || [],
    [project],
  );

  const filteredTasks = useMemo(
    () =>
      tasks.filter((task: Task) =>
        (task.name || "").toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [tasks, searchQuery],
  );

  return (
    <SpotIncentivePickerShell
      open={open}
      onClose={() => onOpenChange(false)}
      presentation={presentation}
      title="Select task"
      description="Choose a task to adjust points against"
      size={{
        width: "28rem",
        height: "34rem",
        minWidth: "min(90vw, 28rem)",
        minHeight: "min(90vh, 34rem)",
        maxWidth: "90vw",
        maxHeight: "90vh",
      }}
      dialogClassName="sm:max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (localSelectedId) onTaskSelect(localSelectedId);
              onOpenChange(false);
            }}
            disabled={!localSelectedId}
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            Done
          </Button>
        </div>
      }
    >
      <div className={cn("flex min-h-0 flex-1 flex-col space-y-3", isInline && "h-full")}>
        <div className="relative shrink-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter tasks…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 rounded-full border-input bg-muted pl-9 text-sm shadow-none"
          />
        </div>

        <ScrollArea className={cn(isInline ? "min-h-0 flex-1" : "h-64")}>
          {filteredTasks.length > 0 ? (
            <div className="space-y-2 p-1 pr-3">
              {filteredTasks.map((task: Task) => {
                const taskId = Number(task.id);
                const isSelected = localSelectedId === taskId;
                return (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => setLocalSelectedId(taskId)}
                    className={cn(
                      "w-full rounded-lg border-2 p-0.5 text-left transition-colors",
                      isSelected
                        ? "border-primary/40 bg-primary/10"
                        : "border-transparent hover:bg-muted/50",
                    )}
                  >
                    <TaskRenderer record={task} />
                  </button>
                );
              })}
            </div>
          ) : tasks.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground">
              No eligible tasks. Only tasks with events on or before today can have points adjusted.
            </p>
          ) : (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground">
              No tasks match your search.
            </p>
          )}
        </ScrollArea>
      </div>
    </SpotIncentivePickerShell>
  );
};

export default SpotIncentiveTaskSelector;
